import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { SearchResultCard } from './SearchResultCard'

interface Restaurant {
  id: string
  name: string
  bestmenu: string
  bestmenu1?: string
  bestmenu2?: string
  bestmenu3?: string
  pickup_instructions: string
  is_open: boolean
  is_new?: boolean
  store_type: string
  created_at: string
  image_url?: string
  products?: Array<{
    id: string
    name: string
    image_url?: string
  }>
}

interface SearchResultsProps {
  query: string
  onResultClick: (restaurantId: string) => void
  className?: string
}

export function SearchResults({ query, onResultClick, className }: SearchResultsProps) {
  const [results, setResults] = useState<Restaurant[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const searchRestaurants = async () => {
      if (!query.trim()) {
        setResults([])
        return
      }

      setIsLoading(true)
      setError(null)

      try {
        // Import the new search service
        const { restaurantSearchService } = await import('@/services/restaurant-search.service')
        
        // Use the enhanced search with fuzzy matching and synonyms
        const result = await restaurantSearchService.searchRestaurants(query, { 
          hasProducts: true,
          isOpen: undefined // Show all results, both open and closed
        })
        
        // Convert service Restaurant[] to local Restaurant[]
        const localResults: Restaurant[] = result.map(restaurant => ({
          id: restaurant.id,
          name: restaurant.name,
          bestmenu: restaurant.bestmenu || '',
          bestmenu1: restaurant.bestmenu1,
          bestmenu2: restaurant.bestmenu2,
          bestmenu3: restaurant.bestmenu3,
          pickup_instructions: restaurant.pickup_instructions || '',
          is_open: restaurant.is_open || false,
          is_new: restaurant.is_new || false,
          store_type: restaurant.store_type || 'cafe',
          created_at: restaurant.created_at || new Date().toISOString(),
          image_url: restaurant.image_url,
          products: restaurant.products?.map(product => ({
            id: product.id,
            name: product.name,
            image_url: product.image_url
          }))
        }));
        setResults(localResults)
      } catch (err) {
        console.error('Search error:', err)
        setError('검색 중 오류가 발생했습니다')
      } finally {
        setIsLoading(false)
      }
    }

    // 디바운싱: 사용자가 타이핑을 멈춘 후 300ms 후에 검색
    const timer = setTimeout(() => {
      searchRestaurants()
    }, 300)

    return () => clearTimeout(timer)
  }, [query])

  if (!query.trim()) {
    return null
  }

  if (isLoading) {
    return (
      <div className={cn("flex items-center justify-center py-12", className)}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#86DB89]"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={cn("px-4 py-12 text-center", className)}>
        <p className="text-[14px] text-[#FF3B30]">{error}</p>
      </div>
    )
  }

  if (results.length === 0) {
    return (
      <div className={cn("px-4 py-12 text-center", className)}>
        <p className="text-[16px] font-medium text-[#222222] mb-2">
          '{query}'에 대한 검색 결과가 없습니다
        </p>
        <p className="text-[14px] text-[#999999]">
          다른 검색어로 시도해보세요
        </p>
      </div>
    )
  }

  return (
    <div className={cn("bg-white", className)}>
      <div className="px-4 py-3 border-b border-[#F5F5F5]">
        <p className="text-[14px] text-[#666666]">
          검색 결과 {results.length}개
        </p>
      </div>
      
      <div className="divide-y divide-[#F5F5F5]">
        {results.map((restaurant) => (
          <SearchResultCard
            key={restaurant.id}
            id={restaurant.id}
            name={restaurant.name}
            bestmenu={restaurant.bestmenu}
            bestmenu1={restaurant.bestmenu1}
            bestmenu2={restaurant.bestmenu2}
            bestmenu3={restaurant.bestmenu3}
            pickupInstructions={restaurant.pickup_instructions}
            isOpen={restaurant.is_open}
            isNew={restaurant.is_new}
            createdAt={restaurant.created_at}
            storeType={restaurant.store_type}
            imageUrl={restaurant.image_url}
            products={restaurant.products}
            onClick={() => onResultClick(restaurant.id)}
          />
        ))}
      </div>
    </div>
  )
}